import moment, { Moment } from 'moment';

// Singapore timezone utility functions
export const SINGAPORE_TIMEZONE = '+08:00';

/**
 * Creates a moment instance in Singapore timezone
 */
export const singaporeMoment = (date?: string | number | Date): Moment => {
  return moment(date).utcOffset(SINGAPORE_TIMEZONE);
};

/**
 * Converts a date to Singapore timezone
 */
export const toSingaporeTime = (date: string | number | Date): Moment => {
  return moment(date).utcOffset(SINGAPORE_TIMEZONE);
};

/**
 * Gets the current date in Singapore timezone
 */
export const nowInSingapore = (): Moment => {
  return moment().utcOffset(SINGAPORE_TIMEZONE);
};

/**
 * Formats a date for display in Singapore timezone
 */
export const formatSingaporeDate = (date: string | number | Date, format = 'YYYY-MM-DD'): string => {
  return singaporeMoment(date).format(format);
};

/**
 * Gets timestamp for start of day in Singapore timezone
 */
export const startOfDaySingapore = (date: string | number | Date): number => {
  return singaporeMoment(date).startOf('day').valueOf();
};

/**
 * Gets timestamp for end of day in Singapore timezone  
 */
export const endOfDaySingapore = (date: string | number | Date): number => {
  return singaporeMoment(date).endOf('day').valueOf();
};

/**
 * Validates if a date range is within allowed limits (max 93 days)
 */
export const validateDateRange = (startDate: Moment, endDate: Moment): { isValid: boolean; message?: string } => {
  const now = nowInSingapore();
  const maxPastDays = 93; // 31 * 3 days
  const earliestAllowed = now.clone().subtract(maxPastDays, 'days');

  if (startDate.isAfter(now, 'day')) {
    return { isValid: false, message: 'Start date cannot be in the future' };
  }

  if (endDate.isAfter(now, 'day')) {
    return { isValid: false, message: 'End date cannot be in the future' };
  }

  if (startDate.isBefore(earliestAllowed, 'day')) {
    return { isValid: false, message: `Date range cannot exceed ${maxPastDays} days in the past` };
  }

  if (startDate.isAfter(endDate, 'day')) {
    return { isValid: false, message: 'Start date must be before or equal to end date' };
  }

  const daysDiff = endDate.diff(startDate, 'days');

  if (daysDiff > maxPastDays) {
    return { isValid: false, message: `Date range cannot exceed ${maxPastDays} days` };
  }

  return { isValid: true };
};

/**
 * Gets display text for timezone
 */
export const getTimezoneDisplayText = (): string => {
  return 'Singapore Time (GMT+8)';
};
